<template>
  <div class="page-login">
    <!-- ログインフォーム -->
    <div id="wrapper1">
      <div class="com_login1">
        <div>
          <p class="copy1"></p>
          <img :src="loginImage" alt="" class="image1" />
        </div>
        <div>
          <img :src="loginLogoImage" :alt="altText" class="id1" />
          <div class="form1">
            <input
              id="loginId"
              v-model="loginId"
              type="text"
              placeholder="ログインID"
              maxlength="255"
            />
            <div class="group1">
              <div class="ch_input_password1">
                <input
                  :type="isPasswordVisible ? 'text' : 'password'"
                  placeholder="パスワード"
                  id="newPassword"
                  v-model="password"
                  maxlength="255"
                />
                <img
                  class="pwdIcon"
                  :src="currentPassworIcon"
                  @click="togglePasswordVisibility"
                />
              </div>
            </div>
            <!-- エラーメッセージの表示エリア -->
            <div v-if="errorMessage" class="error-message">
              {{ errorMessage }}
            </div>
            <button
              type="submit"
              id="btnLogin"
              @click="login"
              :disabled="isProcessing"
            >
              ログイン
            </button>
          </div>
          <ul class="com_link_list1">
            <li>
              <router-link
                to="/password-reset"
                class="com_link1"
                :disabled="isProcessing"
                >初回ログイン時、またはパスワード設定/変更はこちら</router-link
              >
            </li>
          </ul>
          <p class="copyright1">© 2025 Kaunet</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { AxiosError } from "axios";
import { defineComponent, ref, computed } from "vue";
import { useRouter, useRoute } from "vue-router";
import {
  formatMessage,
  outputLoginDebugLogs,
  setUserInfo,
  outputDebugLogs,
  sanitizeInput,
  getLoginInfomation,
} from "@/Common";
import messages from "@/assets/messages.json";
import { AppConstants } from "@/constants/AppConstants";
import apiClient from "@/util/apiClient";
import visibleIcon from "@/assets/common/img/icon-eye1.png";
import hiddenIcon from "@/assets/common/img/icon-eye1-slash.png";

export default defineComponent({
  name: "LoginComponent",
  setup() {
    const loginId = ref("");
    const password = ref("");
    const errorMessage = ref("");
    const router = useRouter();
    const route = useRoute();
    const isPasswordVisible = ref(false);
    const isProcessing = ref(false);

    // --- システム区分に応じてログイン画像を切替（ビルド時に展開される） ---
    const STYLE_MAP: Record<string, string> = {
      "01": require("@/assets/common/img/01/login_top_img1.png"),
      "02": require("@/assets/common/img/02/login_top_img1.png"),
      "03": require("@/assets/common/img/03/login_top_img1.png"),
    };
    const STYLE_LOGO_MAP: Record<string, string> = {
      "01": require("@/assets/common/img/01/login_top_id1.png"),
      "02": require("@/assets/common/img/02/login_top_id1.png"),
      "03": require("@/assets/common/img/03/login_top_id1.png"),
    };
    const STYLE_ALT_MAP: Record<string, string> = {
      "01": "ウィズカウネット エージェントポータル",
      "02": "ベースカウネット エージェントポータル",
      "03": "サプライヤーポータル",
    };
    const systemKbn = process.env.VUE_APP_SYSTEM_KBN;
    const loginImage = STYLE_MAP[systemKbn];
    const loginLogoImage = STYLE_LOGO_MAP[systemKbn];
    const altText = STYLE_ALT_MAP[systemKbn];

    // **** パスワードの表示・非表示の切り替え *****
    const togglePasswordVisibility = () => {
      isPasswordVisible.value = !isPasswordVisible.value;
    };

    // **** パスワードの目のマークのアイコン切替 *****
    const currentPassworIcon = computed(() => {
      return isPasswordVisible.value ? visibleIcon : hiddenIcon;
    });

    // **** 入力チェック処理 *****
    const validateInput = (): boolean => {
      const sanitizedLoginId = sanitizeInput(loginId.value);
      const sanitizedPassword = sanitizeInput(password.value);

      if (!sanitizedLoginId) {
        errorMessage.value = formatMessage(messages.ERR_0001, "ログインID");
        return false;
      } else if (!sanitizedPassword) {
        errorMessage.value = formatMessage(messages.ERR_0001, "パスワード");
        return false;
      } else {
        errorMessage.value = ""; // エラーがない場合はクリア
        return true;
      }
    };

    // **** ログイン処理 *****
    const login = async () => {
      isProcessing.value = true;
      try {
        // 入力チェック
        if (!validateInput()) {
          return;
        }

        // ローカルストレージからトークン情報を削除
        localStorage.removeItem(AppConstants.TOKEN_SESSION_NAME);
        localStorage.removeItem(AppConstants.SYSTEMDATE_SESSION_NAME);
        localStorage.removeItem(AppConstants.BUSINESS_DAYS_SESSION_NAME);

        //ログインAPI
        const response = await apiClient.post("/api/Auth/login", {
          systemKbn: process.env.VUE_APP_SYSTEM_KBN,
          loginId: sanitizeInput(loginId.value),
          password: sanitizeInput(password.value),
        });
        localStorage.setItem(
          AppConstants.TOKEN_SESSION_NAME,
          response.data.token
        );
        localStorage.setItem(
          AppConstants.SYSTEMDATE_SESSION_NAME,
          response.data.systemDate
        );
        // デバックログ出力
        outputDebugLogs(
          "ワンタイムパスワード認証状態: " +
            String(response.data.isAreadyOptAuth)
        );

        if (response.data.isAreadyOptAuth == false) {
          // ワンタイムパスワード未認証状態
          router.push({
            path: "/login-otp",
            query: {
              loginId: sanitizeInput(loginId.value),
              password: sanitizeInput(password.value),
            },
          });
          return;
        }

        // ログイン時のグローバルナビや権限情報などを取得
        const error = await getLoginInfomation(
          response.data.user.agCode,
          response.data.user.userId,
          false
        );

        if (error) {
          errorMessage.value = error;
          return;
        } else {
          errorMessage.value = "";
        }

        // レスポンス情報のユーザー情報をローカルストレージへ格納
        setUserInfo(response);

        // デバックログ出力
        outputLoginDebugLogs(response.data.user);

        // ベースログインフラグがTrueの場合、新規タブを開いてベースログインAPIを呼び出す
        outputDebugLogs(
          "ベースログインフラグ" + String(response.data.user.isBaseLogin)
        );
        if (response.data.user.isBaseLogin == true) {
          const url = formatMessage(
            process.env.VUE_APP_BASE_LOGIN_URL,
            process.env.VUE_APP_SYSTEM_KBN,
            response.data.user.agCode,
            response.data.user.userId,
            response.data.user.baseAgCode,
            response.data.token
          );

          outputDebugLogs("ベースURL:" + url);
          const newTab = window.open(url, "_blank");
          if (newTab) {
            // 新しいウィンドウのロードイベントにフォーカス戻しを設定
            newTab.onload = () => {
              setTimeout(() => {
                newTab.blur();
                window.focus();
              }, 500); // 0.5秒遅延させる
            };
          }
        }

        // 認証成功の場合「redirect」クエリをチェック
        // クエリ中の redirect があればその URL へ、それ以外は /portal-top へ飛ばす
        outputDebugLogs("route.query.redirect:" + route.query.redirect);
        const redirectPath = route.query.redirect;
        if (typeof redirectPath === "string" && redirectPath.trim() !== "") {
          // クエリパラメータに渡されたパスに遷移
          outputDebugLogs("ログイン前の画面に戻る:" + redirectPath);
          router.replace(redirectPath);
        } else {
          // 省略せずポータルトップに飛ばす
          router.replace("/portal-top");
        }
      } catch (error: unknown) {
        const axiosError = error as AxiosError;
        if (axiosError.response) {
          switch (axiosError.response.status) {
            case 401:
              errorMessage.value = messages.ERR_USER002;
              break;
            case 500:
              errorMessage.value = messages.ERR_SYS999;
              break;
            default:
              errorMessage.value = messages.ERR_SYS001;
          }
        } else if (axiosError.request) {
          errorMessage.value = messages.ERR_SYS001;
        } else {
          errorMessage.value = messages.ERR_SYS002;
          if (process.env.VUE_APP_DEBUG === "true") {
            console.log("Error: ", axiosError.message);
          }
        }
      } finally {
        isProcessing.value = false;
      }
    };

    return {
      loginId,
      password,
      errorMessage,
      login,
      validateInput,
      togglePasswordVisibility,
      isPasswordVisible,
      currentPassworIcon,
      isProcessing,
      loginImage,
      loginLogoImage,
      altText,
    };
  },
});
</script>
<style scoped>
input::-ms-reveal {
  visibility: hidden;
}

.page-login {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
}

.form1 .group1 + button {
  margin-top: 25px;
}

.ch_input_password1 {
  display: flex;
  align-items: center;
  position: relative;
  width: 107%;
}
.form1 .ch_input_password1 input {
  margin-bottom: 0;
  flex: 1;
}

.pwdIcon {
  position: absolute;
  right: 40px;
  cursor: pointer;
  height: 50%;
  width: 30px;
}
</style>
